"use client"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { api } from "../utils/auth"

function SignUp() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "", // Added phone field
    role: "client",
  })
  const [message, setMessage] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response = await api.post("/auth/signup", formData)
      setMessage(response.data.message)
      setIsSuccess(true)
      console.log("Signup successful", response.data)

      // Redirect to login page after successful signup
      setTimeout(() => {
        navigate("/login")
      }, 2000)
    } catch (error) {
      console.error("Signup error", error)
      setMessage(error.response?.data.message || "Signup failed. Please try again.")
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="signup-page">
      <style jsx="true">{`
        .signup-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #f9fafb;
          padding: 1rem;
        }
        
        .signup-container {
          background-color: #fff;
          padding: 2.5rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          width: 100%;
          max-width: 28rem;
        }
        
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }
        
        .logo-icon {
          height: 2.5rem;
          width: 2.5rem;
          background-color: #007BFF;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.25rem;
          border-radius: 0.25rem;
        }
        
        .logo-text {
          margin-left: 0.5rem;
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .logo-text-bold {
          font-weight: 700;
        }
        
        .form-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 1.5rem;
          text-align: center;
          color: #333;
        }
        
        .message {
          margin-bottom: 1rem;
          padding: 0.75rem;
          text-align: center;
          border-radius: 0.375rem;
          font-weight: 500;
        }
        
        .message-success {
          background-color: #d1fae5;
          color: #047857;
        }
        
        .message-error {
          background-color: #fee2e2;
          color: #b91c1c;
        }
        
        .form-group {
          margin-bottom: 1.25rem;
        }
        
        .form-label {
          display: block;
          color: #374151;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
        
        .form-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 0.75rem;
          border-radius: 0.375rem;
          transition: border-color 0.15s ease-in-out;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }
        
        .form-select {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 0.75rem;
          border-radius: 0.375rem;
          background-color: white;
          transition: border-color 0.15s ease-in-out;
        }
        
        .form-select:focus {
          outline: none;
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }
        
        .submit-button {
          width: 100%;
          background-color: #f33a3a;
          color: #fff;
          padding: 0.75rem;
          border-radius: 0.375rem;
          cursor: pointer;
          font-weight: bold;
          border: none;
          transition: background-color 0.2s;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .submit-button:hover {
          background-color: #e02e2e;
        }
        
        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .login-link {
          text-align: center;
          margin-top: 1.5rem;
          color: #4b5563;
        }
        
        .login-link a {
          color: #007BFF;
          text-decoration: none;
          font-weight: 500;
        }
        
        .login-link a:hover {
          text-decoration: underline;
        }
        
        .spinner {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 3px solid white;
          width: 1rem;
          height: 1rem;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="signup-container">
        {/* Logo */}
        <div className="logo-container">
          <div className="logo-icon">S</div>
          <span className="logo-text">
            Shopify <span className="logo-text-bold">Scraper</span>
          </span>
        </div>

        <h2 className="form-title">Create Your Account</h2>

        {message && <div className={`message ${isSuccess ? "message-success" : "message-error"}`}>{message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Create a password"
              required
            />
          </div>

          {/* New Phone Number Field */}
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input
              type="tel" // Use type="tel" for phone numbers
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your phone number (optional)"
              // You can add 'required' if you want it to be a mandatory field
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select name="role" value={formData.role} onChange={handleChange} className="form-select">
              <option value="client">Client</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        <p className="login-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}

export default SignUp